import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink } from 'react-router-dom'
import { LOCALES, tierFor, type Locale, type Tier } from '@chess-uno/shared-protocol'
import { fetchProfile } from '../api/competition.js'
import { setSoundEnabled, useSoundEnabled } from '../api/sound.js'
import type { StoredSession } from '../api/session.js'
import { browserLocale, CATALOGS, dismissLanguageHint, languageChosen, useI18n } from '../i18n/index.js'
import { LogoMark } from './Illustrations.js'
import { FeedbackLink } from './Feedback.js'

/** Elo of the logged-in player, cached across pages to avoid flickering. */
const ratingCache = new Map<string, number>()

function useMyRating(session: StoredSession | null, known?: number): number | undefined {
  const [rating, setRating] = useState<number | undefined>(known ?? (session ? ratingCache.get(session.playerId) : undefined))
  useEffect(() => {
    if (known !== undefined) {
      setRating(known)
      if (session) ratingCache.set(session.playerId, known)
      return
    }
    if (!session) return
    let alive = true
    void fetchProfile(session.name, session).then((p) => {
      if (!alive || !p) return
      ratingCache.set(session.playerId, p.player.rating)
      setRating(p.player.rating)
    })
    return () => {
      alive = false
    }
  }, [session, known])
  return rating
}

/** Tier colors (marks and text). */
export const TIER_COLORS: Record<Tier, string> = {
  bronze: '#b8835a',
  silver: '#b9c0cc',
  gold: '#d4b062',
  platinum: '#8fd3c7',
  diamond: '#9cc4ff',
}

/** Tier mark: faceted gem, no emoji. */
export function TierMark({ rating, size = 14 }: { rating: number; size?: number }) {
  const t = tierFor(rating)
  const c = TIER_COLORS[t.tier]
  return (
    <svg className="tier-mark" width={size} height={size} viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 1.2 14.2 6 8 14.8 1.8 6Z" fill={c} opacity="0.22" />
      <path d="M8 1.2 14.2 6 8 14.8 1.8 6Z" fill="none" stroke={c} strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M1.8 6h12.4M5.4 6 8 14.8 10.6 6M5.4 6 8 1.2l2.6 4.8" fill="none" stroke={c} strokeWidth="0.8" opacity="0.8" />
    </svg>
  )
}

type IconName = 'chevron' | 'arrow-left' | 'flame' | 'bolt' | 'link' | 'enter' | 'cpu' | 'swords' | 'share' | 'logout' | 'sound' | 'muted' | 'flag' | 'trophy' | 'check' | 'instagram' | 'facebook'

/** Stroke icons, 16 px, inherited color. */
export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  const paths: Record<IconName, React.ReactNode> = {
    chevron: <path d="m6 3 5 5-5 5" />,
    'arrow-left': <path d="M13 8H3m4-4L3 8l4 4" />,
    flame: <path d="M8 14.5c2.6 0 4.5-1.8 4.5-4.3 0-2.9-2.6-4.3-3.2-7.7C6.8 4.3 5.9 6 6 7.6 5.2 7 4.9 6.2 4.8 5.3 3.9 6.4 3.5 7.9 3.5 10.2c0 2.5 1.9 4.3 4.5 4.3Z" />,
    bolt: <path d="M9 1.5 3.5 9H8l-1 5.5L12.5 7H8z" />,
    link: <path d="M6.6 9.4a3 3 0 0 0 4.2 0l2.1-2.1a3 3 0 0 0-4.2-4.2l-.8.8M9.4 6.6a3 3 0 0 0-4.2 0L3.1 8.7a3 3 0 0 0 4.2 4.2l.8-.8" />,
    enter: <path d="M9.5 3H13v10H9.5M3 8h7m-3-3 3 3-3 3" />,
    cpu: <path d="M5 5h6v6H5zM6.5 1.5V5m3-3.5V5m-3 6v3.5m3-3.5v3.5M1.5 6.5H5m-3.5 3H5m6-3h3.5M11 9.5h3.5" />,
    swords: <path d="M2 2l6 6m0 0 2 2m-2-2-2 2m2-2 2-2m4-4-6 6m6-6L8 2.5M14 2l-.5 5.5M4 12l-2 2m10-2 2 2" />,
    share: <path d="M8 10V2m0 0L5 5m3-3 3 3M3 9v4.5h10V9" />,
    logout: <path d="M6.5 13H3V3h3.5m3 2.5L12 8l-2.5 2.5M12 8H6" />,
    sound: <><path d="M2 6h2.5L8 3v10l-3.5-3H2z" /><path d="M10.5 5a4 4 0 0 1 0 6m2-8a7 7 0 0 1 0 10" /></>,
    muted: <><path d="M2 6h2.5L8 3v10l-3.5-3H2z" /><path d="m10.5 6 4 4m0-4-4 4" /></>,
    flag: <path d="M4 14.5V2m0 .5h8l-1.8 3.2L12 9H4" />,
    check: <path d="m3.5 8.5 3 3 6-7" />,
    instagram: (
      <>
        <rect x="2" y="2" width="12" height="12" rx="3.5" />
        <circle cx="8" cy="8" r="2.7" />
        <path d="M11.5 4.5h.01" />
      </>
    ),
    facebook: <path d="M9.5 14.5V8.6h2l.4-2.3H9.5V5c0-.7.3-1.1 1.1-1.1h1.4V1.9C11.7 1.8 11 1.7 10.2 1.7c-1.7 0-2.9 1-2.9 3v1.6H5.3v2.3h2v5.9" />,
    trophy: <path d="M5 2h6v4a3 3 0 0 1-6 0zM5 3.5H2.5V5A2.5 2.5 0 0 0 5 7.5m6-4h2.5V5A2.5 2.5 0 0 1 11 7.5M8 9v3m-2.5 2.5h5M6.5 12h3" />,
  }
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  )
}

/** Player's initial in a circle, with a stable hue derived from the username. */
export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: size * 0.42, background: `hsl(${h} 22% 26%)`, color: `hsl(${h} 45% 82%)` }}
      aria-hidden="true"
    >
      {name.slice(0, 1).toUpperCase()}
    </span>
  )
}

export function SoundToggle() {
  const enabled = useSoundEnabled()
  const { t } = useI18n()
  const label = enabled ? t.chrome.soundOff : t.chrome.soundOn
  return (
    <button type="button" className="ghost icon-btn sound-toggle" onClick={() => setSoundEnabled(!enabled)} aria-label={label} title={label} aria-pressed={enabled} data-testid="sound-toggle">
      <Icon name={enabled ? 'sound' : 'muted'} />
    </button>
  )
}

/**
 * Link to the page in another language. On an indexable page it is a real
 * link to that version (crawlable, with hreflang); elsewhere the page stays
 * the same and only the language changes.
 */
function LanguageLink({
  to: target,
  className,
  children,
  testId,
  onSelect,
}: {
  to: Locale
  className: string
  children: ReactNode
  testId?: string
  onSelect?: () => void
}) {
  const { t, setLocale, choose, counterpart } = useI18n()
  const href = counterpart(target)
  const label = t.language.names[target]
  if (href) {
    return (
      <Link
        to={href}
        className={className}
        hrefLang={target}
        lang={target}
        title={label}
        onClick={() => {
          choose(target)
          onSelect?.()
        }}
        data-testid={testId}
      >
        {children}
      </Link>
    )
  }
  return (
    <button
      type="button"
      className={className}
      lang={target}
      title={label}
      aria-label={label}
      onClick={() => {
        setLocale(target)
        onSelect?.()
      }}
      data-testid={testId}
    >
      {children}
    </button>
  )
}

/** Language menu: the current language, opening onto all the others. */
export function LanguageSwitch() {
  const { locale, t } = useI18n()
  const menu = useRef<HTMLDetailsElement>(null)
  const close = () => menu.current?.removeAttribute('open')
  // Closes on a click elsewhere or on Escape, like any menu.
  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      if (menu.current && !menu.current.contains(e.target as Node)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [])
  return (
    <details ref={menu} className="lang-menu">
      <summary className="lang-switch" aria-label={t.language.menu} title={t.language.menu} data-testid="lang-switch">
        {locale.toUpperCase()}
      </summary>
      <ul className="lang-list">
        {LOCALES.map((l) => (
          <li key={l}>
            {l === locale ? (
              <span className="lang-item current" lang={l} aria-current="true">
                <span className="lang-code">{l.toUpperCase()}</span>
                {t.language.names[l]}
              </span>
            ) : (
              <LanguageLink to={l} className="lang-item" testId={`lang-${l}`} onSelect={close}>
                <span className="lang-code">{l.toUpperCase()}</span>
                {t.language.names[l]}
              </LanguageLink>
            )}
          </li>
        ))}
      </ul>
    </details>
  )
}

/** Crawlers get no hint: they read each version through its own URL. */
export function isCrawler(): boolean {
  return typeof navigator !== 'undefined' && /bot|crawl|spider|slurp|lighthouse|headless/i.test(navigator.userAgent)
}

/**
 * Offers the visitor's own language when the page is shown in the other one
 * (until they choose). Floating, so it never shifts the page (no layout shift).
 */
function LanguageHint() {
  const { locale } = useI18n()
  const [wanted, setWanted] = useState<Locale | null>(null)
  useEffect(() => {
    const own = browserLocale()
    setWanted(own !== locale && !languageChosen() && !isCrawler() ? own : null)
  }, [locale])
  if (!wanted) return null
  const text = CATALOGS[wanted].language
  return (
    <div className="lang-hint" lang={wanted} role="note" data-testid="lang-hint">
      <span>{text.hint}</span>
      <LanguageLink to={wanted} className="link lang-hint-link">
        {text.hintLink}
      </LanguageLink>
      <button
        type="button"
        className="ghost icon-btn"
        aria-label={text.dismiss}
        onClick={() => {
          dismissLanguageHint()
          setWanted(null)
        }}
      >
        ✕
      </button>
    </div>
  )
}

/** Navigation bar shared by the pages outside a game. */
export function TopBar({ session, rating: known, onLogout }: { session: StoredSession | null; rating?: number; onLogout?: () => void }) {
  const rating = useMyRating(session, known)
  const { t, path } = useI18n()
  return (
    <>
      <header className="topbar">
        <Link to={path('/')} className="wordmark" aria-label={t.chrome.homeAria}>
          <LogoMark size={28} />
          <span className="wordmark-text">
            Chess<span>·</span>Uno
          </span>
        </Link>
        <nav className="topnav">
          <NavLink to={path('/')} end>
            {t.common.home}
          </NavLink>
          <NavLink to={path('/rules')}>{t.chrome.rules}</NavLink>
        </nav>
        <div className="topbar-user">
          {session && (
            <>
              <Link to={`/player/${encodeURIComponent(session.name)}`} className="user-chip" title={t.chrome.viewProfile}>
                <Avatar name={session.name} size={26} />
                <span className="user-name">{session.name}</span>
                {rating !== undefined && (
                  <span className="user-rating">
                    <TierMark rating={rating} size={12} />
                    {rating}
                  </span>
                )}
              </Link>
              <SoundToggle />
              {onLogout && (
                <button type="button" className="ghost icon-btn" onClick={onLogout} title={t.chrome.logout} data-testid="logout">
                  <Icon name="logout" />
                  <span className="sr-only">{t.chrome.logout}</span>
                </button>
              )}
            </>
          )}
          <LanguageSwitch />
        </div>
      </header>
      <LanguageHint />
    </>
  )
}

export const SOCIAL_LINKS = {
  instagram: 'https://www.instagram.com/chess.uno',
  facebook: 'https://www.facebook.com/profile.php?id=61594764099894',
} as const

/** Shared footer: legal links, feedback and social networks (the language is switched from the top bar). */
export function SiteFooter() {
  const { t, path } = useI18n()
  return (
    <footer className="site-footer">
      <span className="footer-brand">
        <LogoMark size={18} /> Chess·Uno © {new Date().getFullYear()}
      </span>
      <nav>
        <Link to={path('/rules')}>{t.chrome.rules}</Link>
        <Link to={path('/mentions-legales')}>{t.chrome.legal}</Link>
        <Link to={path('/confidentialite')}>{t.chrome.privacy}</Link>
        <FeedbackLink />
        <span className="footer-social">
          {(['instagram', 'facebook'] as const).map((network) => (
            <a
              key={network}
              href={SOCIAL_LINKS[network]}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t.chrome[network]}
              title={t.chrome[network]}
              data-testid={`social-${network}`}
            >
              <Icon name={network} size={18} />
            </a>
          ))}
        </span>
      </nav>
    </footer>
  )
}
